<template>
  <layout>
    <page-header :header="$t('general.new_layout_vehicle_day_night_pricing')" />
    <div v-if="!currentDivision" class="content">
      <p class="text-muted">{{ $t('company.select_division') }}</p>
    </div>
    <div v-else class="content vehicle-day-night-pricing">
      <p v-if="loading" class="text-muted py-3">
        {{ $t('general.loading') }}
      </p>
      <template v-else>
        <b-card v-if="vehicles.length === 0" class="mb-0">
          <p class="text-muted mb-0">
            {{ $t('vendor.table.no_records') }}
          </p>
        </b-card>

        <template v-else>
          <div v-if="singleRideMode">
            <day-night-tab-content
              pricing-key="city_taxi_pricing"
              :tabs-data="tabsData"
              :vehicles="vehicles"
              :days="days"
              :saving="saving"
              :days-metric-options="daysMetricOptions"
              :selected-currency="selectedCurrency"
              @save="saveTab"
            />
          </div>
          <b-tabs
            v-else
            v-model="tabIndex"
            content-class="mt-3"
            pills
            card
          >
            <b-tab
              v-for="tab in pricingTabs"
              :key="tab.key"
              :title="tab.title"
            >
              <day-night-tab-content
                :pricing-key="tab.key"
                :tabs-data="tabsData"
                :vehicles="vehicles"
                :days="days"
                :saving="saving"
                :days-metric-options="daysMetricOptions"
                :selected-currency="selectedCurrency"
                @save="saveTab"
              />
            </b-tab>
          </b-tabs>
        </template>
      </template>
    </div>
  </layout>
</template>

<script>
import VehicleDayNightPricingApi from './Api'
import DayNightTabContent from './DayNightTabContent.vue'

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

function emptyTabState() {
  return {
    night_threshold: { start: '', end: '' },
    days_metric: 'fixed',
    vehicleCells: {},
  }
}

export default {
  name: 'VehicleDayNightPricingIndex',

  components: {
    DayNightTabContent,
  },

  data() {
    return {
      loading: false,
      saving: false,
      vehicles: [],
      tabIndex: 0,
      days: DAYS,
      tabsData: {
        pricing: emptyTabState(),
        city_taxi_pricing: emptyTabState(),
        delivery_pricing: emptyTabState(),
      },
    }
  },

  computed: {
    singleRideMode() {
      return !!this.$settings.one_price_ride_type
    },
    pricingTabs() {
      return [
        { key: 'pricing', title: this.$t('enums.ride_type.transfer') },
        { key: 'city_taxi_pricing', title: this.$t('enums.ride_type.city_taxi') },
        { key: 'delivery_pricing', title: this.$t('enums.ride_type.delivery') },
      ]
    },
    daysMetricOptions() {
      return [
        { value: 'fixed', text: this.$t('vehicles.days_metric.fixed') },
        { value: 'surcharge', text: this.$t('vehicles.days_metric.surcharge') },
        { value: 'percent', text: this.$t('vehicles.days_metric.percent') },
      ]
    },
    selectedCurrency() {
      return this.$root.$account.currency ?? this.$settings.base_currency
    },
  },

  watch: {
    currentDivision: {
      handler(div) {
        if (div) {
          this.load()
        } else {
          this.vehicles = []
        }
      },
      immediate: true,
    },
  },

  methods: {
    hydrateTab(pricingKey) {
      const td = this.tabsData[pricingKey]
      const first = this.vehicles[0]
      const p0 = first ? first[pricingKey] || {} : {}

      td.night_threshold = {
        start: p0.night_threshold?.start ?? '',
        end: p0.night_threshold?.end ?? '',
      }
      td.days_metric = p0.days_metric || 'fixed'
      td.vehicleCells = {}

      for (const v of this.vehicles) {
        const p = v[pricingKey] || {}
        const days = p.days || {}
        this.$set(td.vehicleCells, v.id, {})
        for (const day of DAYS) {
          const d = days[day] || {}
          this.$set(td.vehicleCells[v.id], day, {
            day: d.day != null && d.day !== '' ? d.day : '',
            night: d.night != null && d.night !== '' ? d.night : '',
          })
        }
      }
    },

    hydrateAllTabs() {
      const keys = this.singleRideMode
        ? ['city_taxi_pricing']
        : this.pricingTabs.map(t => t.key)
      for (const key of keys) {
        this.hydrateTab(key)
      }
    },

    async load() {
      if (!this.currentDivision) {
        return
      }
      this.loading = true
      try {
        const api = new VehicleDayNightPricingApi().setParameter(
          'division_id',
          this.currentDivision.id,
        )
        const res = await this.$uiBlocker(api.fetchList(), this.$el)
        this.vehicles = Array.isArray(res.data) ? res.data : []
        this.hydrateAllTabs()
      } catch (e) {
        this.$toast.error(e?.data?.message || e?.message || 'Error')
      } finally {
        this.loading = false
      }
    },

    normalizeNum(val) {
      if (val === '' || val === null || val === undefined) {
        return null
      }
      const n = Number(val)
      return Number.isFinite(n) ? n : null
    },

    buildVehiclesPayload(pricingKey) {
      const td = this.tabsData[pricingKey]
      const vehiclesPayload = {}
      for (const v of this.vehicles) {
        const cells = td.vehicleCells[v.id] || {}
        const days = {}
        for (const day of DAYS) {
          const c = cells[day] || { day: '', night: '' }
          days[day] = {
            day: this.normalizeNum(c.day),
            night: this.normalizeNum(c.night),
          }
        }
        vehiclesPayload[v.id] = { days }
      }
      return vehiclesPayload
    },

    async saveTab(pricingKey) {
      if (!this.currentDivision || !this.$allows('vehicles.update')) {
        return
      }
      this.saving = true
      try {
        const td = this.tabsData[pricingKey]
        const api = new VehicleDayNightPricingApi()
        const res = await this.$uiBlocker(
          api.bulkUpdate({
            division_id: this.currentDivision.id,
            pricing_key: pricingKey,
            night_threshold: {
              start: td.night_threshold.start || null,
              end: td.night_threshold.end || null,
            },
            days_metric: td.days_metric,
            vehicles: this.buildVehiclesPayload(pricingKey),
          }),
          this.$el,
        )
        this.$toast.success(res.message)
        await this.load()
      } catch (e) {
        if (e?.data?.errors) {
          this.$toast.error(
            Object.values(e.data.errors)
              .flat()
              .join(', '),
          )
        } else {
          this.$toast.error(e?.data?.message || e?.message || 'Error')
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.vehicle-day-night-pricing >>> .card-header-pills {
  margin: -0.25rem 0;
}
</style>
